import React, { useState, useRef, useEffect } from 'react';
import { UploadCloud, AlertCircle, FileVideo, Play, Pause, Check } from 'lucide-react';
import type { UserQuotaInfo } from '@vidly/types';
import { toast } from './Toast';

interface UploaderProps {
  quota: UserQuotaInfo | null;
  onFileSelected: (file: File) => void;
  uploadProgress: number; // 0 - 100
  isUploading: boolean;
}

interface VideoMetadata {
  duration: number;
  width: number;
  height: number;
  aspectRatio: string;
}

export const Uploader: React.FC<UploaderProps> = ({
  quota,
  onFileSelected,
  uploadProgress,
  isUploading
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [meta, setMeta] = useState<VideoMetadata | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const maxSizeBytes = quota?.maxUploadSizeBytes || 1024 * 1024 * 1024; // 1 GB default

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const formatSeconds = (totalSeconds: number): string => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.floor(totalSeconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const validateAndHandleFile = (file: File) => {
    setError(null);

    const validTypes = ['video/mp4', 'video/quicktime', 'video/x-m4v', 'video/webm'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp4|mov|m4v|webm)$/i)) {
      const msg = 'Formato no compatible. Por favor sube MP4, MOV o WebM.';
      setError(msg);
      toast.error('Formato no válido', msg);
      return;
    }

    if (file.size > maxSizeBytes) {
      const maxMb = Math.round(maxSizeBytes / (1024 * 1024));
      const msg = `El archivo pesa ${(file.size / (1024 * 1024)).toFixed(1)} MB. El límite de tu plan es ${maxMb} MB.`;
      setError(msg);
      toast.error('Archivo demasiado grande', msg);
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setSelectedFile(file);

    const tempVideo = document.createElement('video');
    tempVideo.preload = 'metadata';
    tempVideo.src = url;
    tempVideo.onloadedmetadata = () => {
      const width = tempVideo.videoWidth;
      const height = tempVideo.videoHeight;
      const duration = tempVideo.duration || 0;
      let ratio = '16:9';
      if (width && height) {
        const r = width / height;
        if (Math.abs(r - 9 / 16) < 0.1) ratio = '9:16 (Vertical)';
        else if (Math.abs(r - 1) < 0.1) ratio = '1:1 (Cuadrado)';
        else if (Math.abs(r - 16 / 9) < 0.1) ratio = '16:9';
        else ratio = `${width}x${height}`;
      }
      setMeta({ duration, width, height, aspectRatio: ratio });
      toast.info('Vídeo detectado', `${formatSeconds(duration)} • ${ratio}`);
    };

    onFileSelected(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndHandleFile(e.dataTransfer.files[0]);
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  return (
    <div className="w-full space-y-4 text-left">
      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
        className="hidden"
        disabled={isUploading}
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) {
            validateAndHandleFile(e.target.files[0]);
          }
        }}
      />

      {/* Main Upload Dropzone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !isUploading && inputRef.current?.click()}
        className={`rounded-2xl border border-dashed p-8 sm:p-14 text-center cursor-pointer transition-all ${
          dragActive
            ? 'border-zinc-400 bg-zinc-900/60'
            : 'border-zinc-800 hover:border-zinc-700 bg-zinc-950/40 hover:bg-zinc-900/20'
        } ${isUploading ? 'pointer-events-none opacity-80' : ''}`}
      >
        <div className="flex flex-col items-center justify-center space-y-3">
          <div className="w-12 h-12 rounded-xl bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-300">
            {selectedFile ? (
              <FileVideo className="w-6 h-6 text-zinc-100" />
            ) : (
              <UploadCloud className="w-6 h-6" />
            )}
          </div>

          <div className="max-w-full px-2">
            <h3 className="text-sm font-medium text-zinc-100 truncate max-w-[240px] sm:max-w-none mx-auto">
              {selectedFile ? selectedFile.name : 'Toca aquí para seleccionar tu vídeo'}
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              {selectedFile
                ? `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB • Listo para procesar`
                : 'o arrastra y suelta el archivo'}
            </p>
          </div>

          <div className="pt-1 flex flex-wrap items-center justify-center gap-2 text-[11px]">
            <span className="px-2.5 py-0.5 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-400">
              MP4 · MOV · WebM
            </span>
            <span className="px-2.5 py-0.5 rounded-full bg-zinc-900 border border-zinc-800 text-zinc-400">
              Límite: {quota?.plan === 'premium' ? '5 GB' : '1 GB'}
            </span>
          </div>
        </div>

        {/* Upload Progress Bar */}
        {isUploading && (
          <div className="mt-6 max-w-sm mx-auto space-y-2">
            <div className="flex justify-between text-xs text-zinc-400 font-medium">
              <span>Subiendo vídeo...</span>
              <span>{Math.round(uploadProgress)}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-zinc-900 overflow-hidden">
              <div
                className="h-full bg-zinc-100 transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Video Preview Card */}
      {previewUrl && (
        <div className="p-4 rounded-xl border border-zinc-800/80 bg-zinc-900/30 flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative w-full sm:w-48 aspect-video rounded-lg overflow-hidden bg-black border border-zinc-800 shrink-0 group">
            <video
              ref={videoRef}
              src={previewUrl}
              className="w-full h-full object-cover"
              onEnded={() => setIsPlaying(false)}
            />
            <button
              onClick={togglePlay}
              className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/20 transition-all"
            >
              <div className="w-8 h-8 rounded-full bg-white/90 text-zinc-950 flex items-center justify-center shadow">
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </div>
            </button>
          </div>

          <div className="flex-1 w-full space-y-2">
            <p className="text-xs font-medium text-zinc-200 truncate">{selectedFile?.name}</p>
            {meta && (
              <div className="flex flex-wrap gap-2 text-xs text-zinc-400">
                <span className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800">
                  {formatSeconds(meta.duration)}
                </span>
                <span className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800">
                  {meta.width} × {meta.height}
                </span>
                <span className="px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800">
                  {meta.aspectRatio}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error notification */}
      {error && (
        <div className="p-3 rounded-lg bg-red-950/30 border border-red-800/40 text-xs text-red-300 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
